// src/admin/ClientLimitDialog.tsx
/**
 * "Limit to [Clients]: All / pick…" for one member (spec 2026-10-29 §6 "Admin dashboard → Users"). The View is
 * pure (node vitest); the container loads the org's Trainer lines (with Retry) and saves the scope. Trainer applies
 * the change within 60 s.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { adminApi, type AdminProductLine, type AdminUser } from '../services/adminApi';
import {
  MAX_LIMIT_LINES,
  mapScopeUpdateFailure,
  pickableLines,
  pluralLabel,
  scopeRequest,
  unknownSelectedIds,
  type ClientLimitMode,
} from './clientLimits';
import { useDialogEscape } from './useDialogEscape';

const ALERT_CLASS = 'rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive';

export interface ClientLimitDialogViewProps {
  memberLabel: string;
  topLabel: string;
  lines: AdminProductLine[];
  linesLoading: boolean;
  linesError: string | null;
  /** Null until staff choose, when the stored limit cannot be read. */
  mode: ClientLimitMode | null;
  selected: ReadonlySet<string>;
  storedMalformed: boolean;
  error: string | null;
  submitting: boolean;
  onModeChange: (mode: ClientLimitMode) => void;
  onToggle: (lineId: string) => void;
  onRetryLines: () => void;
  onSubmit: () => void;
  onClose: () => void;
}

export function ClientLimitDialogView(props: ClientLimitDialogViewProps) {
  const { memberLabel, topLabel, lines, linesLoading, linesError, mode, selected, storedMalformed, error, submitting } = props;
  const canPick = !linesLoading && !linesError;
  const tooMany = mode === 'limited' && selected.size > MAX_LIMIT_LINES;
  const canSubmit = !submitting && (mode === 'all' || (mode === 'limited' && canPick && selected.size > 0 && !tooMany));
  const plural = pluralLabel(topLabel);
  const rows = pickableLines(lines, selected);
  const unknown = unknownSelectedIds(lines, selected);

  return (
    <Modal open onOverlayClick={() => { if (!submitting) props.onClose(); }}>
      <Modal.Content maxWidth="lg" aria-labelledby="client-limit-title">
        <Modal.Header className="px-6 py-4">
          <h2 id="client-limit-title" className="text-lg font-medium text-foreground [overflow-wrap:anywhere]">
            Limit {memberLabel} to {plural}
          </h2>
        </Modal.Header>
        <Modal.Body className="px-6 py-4">
          <div className="flex flex-col gap-3">
            {storedMalformed && (
              <div role="alert" className={ALERT_CLASS}>
                The saved limit cannot be read, so Trainer is denying this person. Choose All or pick again.
              </div>
            )}
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="radio"
                name="limit-mode"
                value="all"
                checked={mode === 'all'}
                autoFocus={mode !== 'limited'}
                onChange={() => props.onModeChange('all')}
              />
              All {plural}
            </label>
            <label className="flex min-h-11 items-center gap-2 text-sm">
              <input
                type="radio"
                name="limit-mode"
                value="limited"
                checked={mode === 'limited'}
                // A stored limit keeps its radio enabled while lines load, so it can take the initial focus.
                disabled={!canPick && mode !== 'limited'}
                autoFocus={mode === 'limited'}
                onChange={() => props.onModeChange('limited')}
              />
              Only these {plural}
            </label>

            {linesLoading && <Spinner size={16} label={`Loading ${plural}…`} />}
            {linesError && (
              <div role="alert" className={`${ALERT_CLASS} flex items-center justify-between gap-2`}>
                <span>{linesError}</span>
                <Button type="button" variant="ghost" size="sm" className="min-h-11" onClick={props.onRetryLines}>Retry</Button>
              </div>
            )}

            {mode === 'limited' && canPick && (
              <p className="text-sm text-muted-foreground">Picking one of the {plural} includes all of its products.</p>
            )}
            {mode === 'limited' && canPick && (
              <ul className="flex flex-col gap-1 pl-6">
                {rows.map((line) => (
                  <li key={line.id} className={line.depth === 1 ? 'pl-6' : undefined}>
                    {line.selectable ? (
                      <label className="flex min-h-11 items-center gap-2 text-sm [overflow-wrap:anywhere]">
                        <input
                          type="checkbox"
                          id={`limit-line-${line.id}`}
                          checked={selected.has(line.id)}
                          onChange={() => props.onToggle(line.id)}
                        />
                        {line.name}{line.archived ? ' (archived)' : ''}
                      </label>
                    ) : (
                      <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{line.name} (archived)</span>
                    )}
                  </li>
                ))}
                {unknown.map((id) => (
                  <li key={id}>
                    <label className="flex min-h-11 items-center gap-2 text-sm text-muted-foreground [overflow-wrap:anywhere]">
                      <input type="checkbox" id={`limit-line-${id}`} checked onChange={() => props.onToggle(id)} />
                      Unknown ({id})
                    </label>
                  </li>
                ))}
                {rows.length === 0 && unknown.length === 0 && (
                  <li className="text-sm text-muted-foreground">This organization has no {plural} yet.</li>
                )}
              </ul>
            )}

            {tooMany && (
              <div role="alert" className={ALERT_CLASS}>
                A limit can name at most {MAX_LIMIT_LINES}; {selected.size} are ticked. Untick some, or choose All.
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              A limit takes effect in Trainer within a minute. Limited people cannot invite others or manage members.
            </p>
            {error && <div role="alert" className={`${ALERT_CLASS} [overflow-wrap:anywhere]`}>{error}</div>}
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
          <Button variant="secondary" className="min-h-11" onClick={props.onClose} disabled={submitting}>Cancel</Button>
          <Button className="min-h-11" onClick={props.onSubmit} disabled={!canSubmit}>{submitting ? 'Saving…' : 'Save'}</Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

interface ClientLimitDialogProps {
  orgId: string;
  user: AdminUser;
  onSaved: (saved: AdminUser) => void;
  onClose: () => void;
}

/** Keyed by organization and member, so a different target never inherits another member's mode or picks. */
export function ClientLimitDialog(props: ClientLimitDialogProps) {
  return <ClientLimitDialogContainer key={`${props.orgId}:${props.user.id}`} {...props} />;
}

function ClientLimitDialogContainer({ orgId, user, onSaved, onClose }: ClientLimitDialogProps) {
  const stored = user.productLineScope;
  const [lines, setLines] = useState<AdminProductLine[]>([]);
  const [topLabel, setTopLabel] = useState('Product'); // the API's default label until Trainer answers
  const [linesLoading, setLinesLoading] = useState(true);
  const [linesError, setLinesError] = useState<string | null>(null);
  const [mode, setMode] = useState<ClientLimitMode | null>(
    stored?.mode === 'malformed' ? null : stored?.mode === 'limited' ? 'limited' : 'all'
  );
  const [selected, setSelected] = useState<Set<string>>(
    new Set(stored?.mode === 'limited' ? stored.lineIds ?? [] : [])
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useDialogEscape(onClose, !submitting);

  const loadLines = useCallback(() => {
    let cancelled = false;
    setLinesLoading(true);
    setLinesError(null);
    adminApi.getTrainerProductLines(orgId)
      .then((data) => {
        if (cancelled) return;
        if (!data.available) {
          setLinesError('Could not load this organization’s lines from Trainer.');
          return;
        }
        setLines(data.lines);
        setTopLabel(data.topLabel);
      })
      .catch((err: unknown) => {
        console.error('Failed to load Trainer product lines:', err);
        if (!cancelled) setLinesError('Could not load this organization’s lines from Trainer.');
      })
      .finally(() => { if (!cancelled) setLinesLoading(false); });
    return () => { cancelled = true; };
  }, [orgId]);

  useEffect(() => loadLines(), [loadLines]);

  const toggle = (lineId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId); else next.add(lineId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!mode) return;
    setSubmitting(true);
    setError(null);
    try {
      const saved = await adminApi.updateUserProductLineScope(orgId, user.id, scopeRequest(mode, selected));
      onSaved(saved);
    } catch (err: unknown) {
      setError(mapScopeUpdateFailure(err));
      setSubmitting(false);
    }
  };

  return (
    <ClientLimitDialogView
      memberLabel={user.name ? `${user.name} (${user.email})` : user.email}
      topLabel={topLabel}
      lines={lines}
      linesLoading={linesLoading}
      linesError={linesError}
      mode={mode}
      selected={selected}
      storedMalformed={stored?.mode === 'malformed'}
      error={error}
      submitting={submitting}
      onModeChange={setMode}
      onToggle={toggle}
      onRetryLines={() => { loadLines(); }}
      onSubmit={() => void handleSubmit()}
      onClose={onClose}
    />
  );
}
